"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { ShoppingCart, ChevronDown, Phone, Mail } from "lucide-react";
import type { Category } from "@/lib/products";
import { useCart } from "@/context/CartContext";

const navLinks = [
  { label: "Home", href: "/" },
  { label: "About", href: "/about" },
  { label: "Services", href: "/services" },
  { label: "Industry", href: "/industry" }
];
const contactInfo = [
  {
    icon: Phone,
    text: '+ (256) 754909670',
  },
  {
    icon: Mail,
    text: 'contact@kasaiequipment.com',
  },
];

const Navbar = ({ categories }: { categories: Category[] }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const pathname = usePathname();
  const { count, setMiniOpen } = useCart();

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="fixed top-0 left-0 right-0 z-50 glass"
    >

      <div className="w-full bg-primary">
        <div className="container mx-auto px-4 py-1.5 flex items-center justify-between gap-3 overflow-hidden">
          {/* Exclusive Dealer Info */}
          <a
            href="https://www.engirise.africa/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex min-w-0 shrink-0 items-center gap-2 md:gap-[14px] cursor-pointer"
          >
            <p className="font-poppins text-sm sm:text-menu text-white/90 whitespace-nowrap tracking-wider">
              Exclusive Dealer Of
            </p>

            <div className="h-4 md:h-[22px] w-px bg-white/20 hidden md:block" />

            <Image
              src="/Engiriselogo.svg"
              alt="Engirise Logo"
              width={100}
              height={24}
              className="h-[16px] md:h-[20px] w-auto"
            />
          </a>

          {/* Contact Information */}
          <div className="flex items-center gap-4 sm:gap-5 md:gap-[32px]">
            {contactInfo.map((contact, index) => {
              const IconComponent = contact.icon;
              const isPhone = contact.text.includes("+");
              const href = isPhone
                ? `tel:${contact.text.replace(/\s+/g, "")}`
                : `mailto:${contact.text}`;

              return (
                <a
                  key={index}
                  href={href}
                  className="flex items-center gap-2 group transition-all duration-300"
                >
                  <IconComponent className="w-5 h-5 sm:w-4 sm:h-4 md:w-[18px] md:h-[18px] text-secondary group-hover:scale-110 transition-transform" />

                  <span className="hidden sm:block font-poppins font-medium text-sm text-white hover:text-tsavo-golden whitespace-nowrap">
                    {contact.text}
                  </span>
                </a>
              );
            })}
          </div>
        </div>
      </div>
      <div className="container mx-auto py-2.5 sm:py-3 px-4">
        <div className="flex items-center justify-between ">


          <Link href="/">
            <Image
              src="/assets/kasai-logo.webp"
              alt="Kasai Equipment logo"
              width={150}
              height={48}
              priority
              className="h-10 sm:h-12 w-auto"
            />
          </Link>

          {/* Desktop */}
          <div className="hidden lg:flex items-center gap-7">
            <Link
              href="/"
              className={`text-sm font-semibold tracking-wide transition-colors duration-200 ${pathname === "/" ? "text-primary" : "text-foreground/80 hover:text-primary"
                }`}
            >
              Home
            </Link>

            {/* Products dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setProductsOpen(true)}
              onMouseLeave={() => setProductsOpen(false)}
            >
              <Link
                href="/products"
                className={`flex items-center gap-1 text-sm font-semibold tracking-wide transition-colors duration-200 ${pathname.startsWith("/products") ? "text-primary" : "text-foreground/80 hover:text-primary"
                  }`}
              >
                Products
                <ChevronDown className="w-3.5 h-3.5" />
              </Link>
              <AnimatePresence>
                {productsOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.15 }}
                    className="absolute top-full left-1/2 -translate-x-1/2 pt-3"
                  >
                    <div className="bg-card border border-border rounded-md shadow-xl py-2 min-w-[220px]">
                      {categories.map((cat) =>
                        cat.active ? (
                          <Link
                            key={cat.slug}
                            href={`/products/${cat.slug}`}
                            className="block px-5 py-2.5 text-sm font-semibold text-foreground hover:bg-primary hover:text-primary-foreground transition"
                          >
                            {cat.name}
                          </Link>
                        ) : (
                          <span
                            key={cat.slug}
                            aria-disabled="true"
                            className="block cursor-not-allowed px-5 py-2.5 text-sm font-semibold text-muted-foreground/50"
                          >
                            {cat.name}
                          </span>
                        )
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {navLinks.slice(1).map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className={`text-sm font-semibold tracking-wide transition-colors duration-200 ${pathname === link.href ? "text-primary" : "text-foreground/80 hover:text-primary"
                  }`}
              >
                {link.label}
              </Link>
            ))}

            <button
              onClick={() => setMiniOpen(true)}
              className="relative p-2 text-foreground hover:text-primary transition"
              aria-label="Open cart"
            >
              <ShoppingCart className="w-5 h-5" strokeWidth={2} />
              {count > 0 && (
                <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-sm font-bold min-w-5 h-5 px-1 rounded-full flex items-center justify-center leading-none">
                  {count}
                </span>
              )}
            </button>

            <Link
              href="/contact"
              className="bg-primary text-primary-foreground px-5 py-2.5 rounded-sm text-sm font-bold hover:glow-primary transition-all duration-300 hover:-translate-y-0.5"
            >
              Get a Quote
            </Link>
          </div>

          {/* Mobile actions */}
          <div className="lg:hidden flex items-center gap-2">
            <button
              onClick={() => setMiniOpen(true)}
              className="relative p-2 text-foreground"
              aria-label="Open cart"
            >
              <ShoppingCart className="w-5 h-5" />
              {count > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-primary text-primary-foreground text-sm font-bold min-w-5 h-5 px-1 rounded-full flex items-center justify-center leading-none">
                  {count}
                </span>
              )}
            </button>
            <button
              className="flex flex-col gap-1.5 p-2"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
              aria-expanded={mobileOpen}
            >
              <span className={`block w-6 h-0.5 bg-foreground transition-transform ${mobileOpen ? "rotate-45 translate-y-2" : ""}`} />
              <span className={`block w-6 h-0.5 bg-foreground transition-opacity ${mobileOpen ? "opacity-0" : ""}`} />
              <span className={`block w-6 h-0.5 bg-foreground transition-transform ${mobileOpen ? "-rotate-45 -translate-y-2" : ""}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden glass overflow-hidden"
          >
            <div className="flex max-h-[calc(100vh-112px)] flex-col gap-3 overflow-y-auto p-4 sm:p-6">
              <Link href="/" onClick={() => setMobileOpen(false)} className="text-base font-semibold text-foreground/90">Home</Link>
              <div>
                <p className="text-base font-semibold text-foreground/90 mb-2">Products</p>
                <div className="pl-3 flex flex-col gap-2">
                  {categories.map((cat) =>
                    cat.active ? (
                      <Link
                        key={cat.slug}
                        href={`/products/${cat.slug}`}
                        onClick={() => setMobileOpen(false)}
                        className="text-sm text-muted-foreground hover:text-primary"
                      >
                        {cat.name}
                      </Link>
                    ) : (
                      <span
                        key={cat.slug}
                        aria-disabled="true"
                        className="cursor-not-allowed text-sm text-muted-foreground/50"
                      >
                        {cat.name}
                      </span>
                    )
                  )}
                </div>
              </div>
              {navLinks.slice(1).map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="text-base font-semibold text-foreground/90 hover:text-primary"
                >
                  {link.label}
                </Link>
              ))}
              <Link
                href="/contact"
                onClick={() => setMobileOpen(false)}
                className="bg-primary text-primary-foreground px-5 py-3 rounded-sm text-sm font-bold text-center"
              >
                Get a Quote
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
